import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Check, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTime } from '../../utils/dateTime';
import api from '../../services/api';
import Layout from '../../components/layout/Layout';
import MobileCardList, { TableColumn } from '../../components/common/MobileCardList';

interface GrnItem {
  id: number;
  tenantId: number;
  grnId: number;
  productDescription: string;
  productCategoryId: number;
  productCategoryName: string;
  itemCode: string;
  unitType: string;
  weightPerItem: number | null;
  batchNo: string;
  goodQty: number;
  cdamQty: number;
  damQty: number;
  palletCount: number;
  storageType: string;
  storageDetails: any[];
  shortExcess: string;
  totalWeight: number;
  remarks: string;
  createdAt: string;
}

interface GrnDetails {
  id: number;
  tenantId: number;
  grnNumber: string;
  gateEntryId: number;
  gateEntryNumber: string;
  clientId: number;
  clientName: string;
  warehouseId: number;
  warehouseName: string;
  status: string;
  createdBy: number;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  approvedBy?: number;
  approvedByName?: string;
  approvedAt?: string;
  rejectedBy?: number;
  rejectedByName?: string;
  rejectedAt?: string;
}

interface Grn {
  id: number;
  tenantId: number;
  grnNumber: string;
  gateEntryId: number;
  gateEntryNumber: string;
  clientId: number;
  clientName: string;
  warehouseId: number;
  warehouseName: string;
  status: string;
  createdBy: number;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface GrnListResponse {
  items: Grn[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const GrnApprovals: React.FC = () => {
  const [selectedGrn, setSelectedGrn] = useState<Grn | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Fetch submitted GRNs
  const { data: grnsData, isLoading } = useQuery<GrnListResponse>({
    queryKey: ['grns', 'submitted'],
    queryFn: async () => {
      const response = await api.get('/grn?status=submitted');
      return response.data.data;
    },
  });

  // Fetch GRN details
  const { data: grnDetails, isLoading: detailsLoading } = useQuery<GrnDetails>({
    queryKey: ['grn', selectedGrn?.id, 'details'],
    queryFn: async () => {
      if (!selectedGrn) return null;
      const response = await api.get(`/grn/${selectedGrn.id}`);
      return response.data.data;
    },
    enabled: !!selectedGrn && showDetailsModal,
  });

  // Fetch GRN items
  const { data: grnItems, isLoading: itemsLoading } = useQuery<GrnItem[]>({
    queryKey: ['grn', selectedGrn?.id, 'items'],
    queryFn: async () => {
      if (!selectedGrn) return [];
      const response = await api.get(`/grn/${selectedGrn.id}/items`);
      return response.data.data;
    },
    enabled: !!selectedGrn && showDetailsModal,
  });

  // Approve GRN mutation
  const approveMutation = useMutation({
    mutationFn: async (grnId: number) => {
      const response = await api.post(`/grn/${grnId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      showToast('GRN approved successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      setShowConfirmModal(false);
      setSelectedGrn(null);
      setShowDetailsModal(false);
    },
    onError: (error: any) => {
      const message = error?.statusCode === 403
        ? "Access Denied: You don't have permission to approve this GRN. Please contact your administrator if you need access."
        : (error?.message || 'Failed to approve GRN');
      showToast(message, 'error');
    },
  });

  // Reject GRN mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ grnId, rejectionReason }: { grnId: number; rejectionReason: string }) => {
      const response = await api.post(`/grn/${grnId}/reject`, { rejectionReason });
      return response.data;
    },
    onSuccess: () => {
      showToast('GRN rejected successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      setShowConfirmModal(false);
      setSelectedGrn(null);
      setShowDetailsModal(false);
      setRejectionReason('');
    },
    onError: (error: any) => {
      const message = error?.statusCode === 403
        ? "Access Denied: You don't have permission to reject this GRN. Please contact your administrator if you need access."
        : (error?.message || 'Failed to reject GRN');
      showToast(message, 'error');
    },
  });

  const handleAction = (action: 'approve' | 'reject', grn: Grn) => {
    setSelectedGrn(grn);
    setConfirmAction(action);
    setRejectionReason('');
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (!selectedGrn || !confirmAction) return;

    if (confirmAction === 'approve') {
      approveMutation.mutate(selectedGrn.id);
    } else {
      if (!rejectionReason.trim()) {
        showToast('Please provide a rejection reason', 'error');
        return;
      }
      rejectMutation.mutate({ grnId: selectedGrn.id, rejectionReason });
    }
  };

  const handleViewDetails = (grn: Grn) => {
    setSelectedGrn(grn);
    setShowDetailsModal(true);
  };

  // Shared by the desktop table and the mobile card list
  const grnApprovalColumns: TableColumn[] = [
    { key: 'grnNumber', label: 'GRN ID' },
    { key: 'clientName', label: 'Client Name' },
    { key: 'warehouseName', label: 'Warehouse' },
    { key: 'createdAt', label: 'Created At', render: (value) => formatDateTime(value) },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, grn) => (
        <div className="flex items-center justify-end gap-3 flex-wrap">
          <button
            onClick={() => handleViewDetails(grn)}
            className="text-brand-600 hover:text-brand-900 inline-flex items-center"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </button>
          <button
            onClick={() => handleAction('approve', grn)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            className="text-green-600 hover:text-green-900 inline-flex items-center disabled:opacity-50"
          >
            <Check className="h-4 w-4 mr-1" />
            Approve
          </button>
          <button
            onClick={() => handleAction('reject', grn)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            className="text-red-600 hover:text-red-900 inline-flex items-center disabled:opacity-50"
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">GRN Approvals</h1>
        <div className="text-sm text-gray-500">
          {grnsData?.meta.total || 0} pending approvals
        </div>
      </div>

      <MobileCardList
        columns={grnApprovalColumns}
        data={grnsData?.items || []}
        emptyMessage="No GRNs pending approval"
      />

      <div className="hidden lg:block bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                GRN ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Client Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Warehouse
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Created At
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {grnsData?.items.map((grn) => (
              <tr key={grn.id} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01] transform transition-all duration-300 border-b border-gray-200 last:border-b-0 group">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                  {grn.grnNumber}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {grn.clientName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {grn.warehouseName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                 {formatDateTime(grn.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleViewDetails(grn)}
                    className="text-brand-600 hover:text-brand-900 inline-flex items-center"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button
                    onClick={() => handleAction('approve', grn)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                    className="text-green-600 hover:text-green-900 inline-flex items-center disabled:opacity-50"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction('reject', grn)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                    className="text-red-600 hover:text-red-900 inline-flex items-center disabled:opacity-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {grnsData?.items.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No GRNs pending approval</p>
          </div>
        )}
      </div>

      {/* Comprehensive Details Modal */}
      {showDetailsModal && selectedGrn && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end justify-center p-3 lg:p-0 lg:block">
          <div className="relative lg:top-4 mx-auto p-5 border w-full lg:w-11/12 max-w-6xl shadow-lg rounded-2xl lg:rounded-md bg-white max-h-[85vh] lg:max-h-[95vh] overflow-y-auto animate-slide-up lg:animate-none">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                GRN Details - {selectedGrn.grnNumber}
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {detailsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
              </div>
            ) : grnDetails ? (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">GRN Number</label>
                      <p className="mt-1 text-sm text-gray-900 font-semibold">{grnDetails.grnNumber}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Gate Entry Number</label>
                      <p className="mt-1 text-sm text-gray-900">{grnDetails.gateEntryNumber}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        grnDetails.status === 'submitted' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : grnDetails.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {grnDetails.status.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Client</label>
                      <p className="mt-1 text-sm text-gray-900">{grnDetails.clientName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Warehouse</label>
                      <p className="mt-1 text-sm text-gray-900">{grnDetails.warehouseName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created By</label>
                      <p className="mt-1 text-sm text-gray-900">{grnDetails.createdByName}</p>
                      <p className="text-xs text-gray-500">{grnDetails.createdByEmail}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created At</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDateTime(grnDetails.createdAt)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDateTime(grnDetails.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">GRN Items</h4>

                  {itemsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
                    </div>
                  ) : (
                    <>
                    <MobileCardList
                      columns={[
                        { key: 'productDescription', label: 'Product Description' },
                        { key: 'productCategoryName', label: 'Category' },
                        { key: 'itemCode', label: 'Item Code' },
                        { key: 'batchNo', label: 'Batch No' },
                        { key: 'unitType', label: 'Unit Type' },
                        { key: 'goodQty', label: 'Good Qty' },
                        { key: 'cdamQty', label: 'CDAM Qty' },
                        { key: 'damQty', label: 'DAM Qty' },
                        { key: 'palletCount', label: 'Pallets' },
                        { key: 'weightPerItem', label: 'Weight/Item', render: (value) => value || '-' },
                        { key: 'totalWeight', label: 'Total Weight' },
                        { key: 'storageType', label: 'Storage Type' },
                        { key: 'shortExcess', label: 'Short/Excess', render: (value) => value || '-' },
                        { key: 'remarks', label: 'Remarks', render: (value) => value || '-' },
                      ]}
                      data={grnItems || []}
                    />
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Product Description
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Category
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Item Code
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Batch No
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Unit Type
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Good Qty
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              CDAM Qty
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              DAM Qty
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Pallets
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Weight/Item
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Total Weight
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Storage Type
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Short/Excess
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Remarks
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {grnItems?.map((item) => (
                            <tr key={item.id} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01] transform transition-all duration-300 border-b border-gray-200 last:border-b-0 group">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.productDescription}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.productCategoryName}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.itemCode}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.batchNo}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.unitType}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.goodQty}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.cdamQty}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.damQty}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.palletCount}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.weightPerItem || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.totalWeight}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.storageType}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.shortExcess || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {item.remarks || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}

                  {grnItems?.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No items found for this GRN</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Failed to load GRN details</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col-reverse gap-3 lg:flex-row lg:justify-end lg:gap-0 lg:space-x-3 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full lg:w-auto min-h-[44px] lg:min-h-0 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => handleAction('reject', selectedGrn)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="w-full lg:w-auto min-h-[44px] lg:min-h-0 px-6 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Reject GRN'}
              </button>
              <button
                onClick={() => handleAction('approve', selectedGrn)}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="w-full lg:w-auto min-h-[44px] lg:min-h-0 px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {approveMutation.isPending ? 'Approving...' : 'Approve GRN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedGrn && confirmAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-end justify-center p-3 lg:p-0 lg:block">
          <div className="relative lg:top-20 mx-auto p-5 border w-full lg:w-96 shadow-lg rounded-2xl lg:rounded-md bg-white max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {confirmAction === 'approve' ? 'Approve GRN' : 'Reject GRN'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to {confirmAction} GRN {selectedGrn.grnNumber}?
                  {confirmAction === 'approve' && (
                    <span className="block mt-2 text-brand-600">
                      This will create inventory records and cannot be undone.
                    </span>
                  )}
                </p>
                {confirmAction === 'reject' && (
                  <div className="mt-4 text-left">
                    <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="rejectionReason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Please provide a reason for rejection..."
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                    confirmAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {approveMutation.isPending || rejectMutation.isPending
                    ? 'Processing...'
                    : confirmAction === 'approve'
                    ? 'Approve'
                    : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default GrnApprovals;